import type { ReactNode } from 'react'
import { cn } from '../lib/utils'
import { blockTheme, hsl } from '../block-theme/blockTheme'
import { QuizMascot } from '../quiz-mascot/quiz-mascot'
import { useFitLines } from './useFitLines'
import { useFitMascot } from './useFitMascot'

interface Props {
  accent?: string
  question?: string
  hint?: string
  headingFont?: string
  /** Живой маскот вместо статичного (приложение подставляет Rive). */
  mascot?: ReactNode
  /** Подсказка автору, пока вопрос пуст: серым, как плейсхолдер поля. */
  placeholder?: string
  /** Не больше стольких строк вопроса: текст сам уменьшается (`useFitLines`). */
  maxQuestionLines?: number
  /** Экран без прокрутки: не влезшее содержимое уменьшает маскота (`useFitMascot`). */
  fitMascot?: boolean
  children: ReactNode
}

/**
 * Сцена интерактивного блока: маскот, вопрос, содержимое. Порт плеерного
 * `QuizShell` (academy-frontend `.../blocks/QuizShell/`): гарнитура Wix Madefor,
 * поле 20px, светлая сцена `--block-quiz-bg`, вопрос 22/600 по центру. Отступ
 * 8px по бокам — `padding-inline` обёртки `.blockWrapper` модалки плеера.
 */
export const QuizShell = ({
  accent = blockTheme.primary,
  question,
  hint,
  headingFont,
  mascot,
  placeholder,
  maxQuestionLines,
  fitMascot = false,
  children,
}: Props) => {
  const shown = question || placeholder || ''
  const { ref: questionRef, fontSize } = useFitLines(shown, maxQuestionLines)
  const fit = useFitMascot(fitMascot)
  return (
    <div
      ref={fit.scrollerRef}
      className="relative z-0 flex h-full min-h-0 flex-1 flex-col overflow-auto p-5 font-madefor"
      style={{
        backgroundColor: blockTheme.quiz.bg,
        paddingBottom: 'calc(20px + var(--verdict-safe-zone, 0px))',
      }}
    >
      <div className="my-auto w-full">
        <div ref={fit.mascotRef}>{mascot ?? <QuizMascot accent={accent} />}</div>
        <p
          ref={questionRef}
          className={cn(
            'shrink-0 whitespace-pre-line text-center text-[22px] font-semibold leading-[1.24]',
            hint ? 'mb-1.5' : 'mb-5',
          )}
          style={{
            color: question || !placeholder ? blockTheme.quiz.text : blockTheme.quiz.mutedText,
            fontFamily: headingFont,
            fontSize,
          }}
        >
          {/* Плеер (QuizShell/index.tsx) рисует question сырым, без фолбэка —
            «Вопрос?» подменял бы пустой вопрос курса выдуманным текстом. */}
          {question || placeholder}
        </p>
        {hint ? (
          <p className="mb-5 text-center text-[13px]" style={{ color: hsl(blockTheme.quizMuted) }}>
            {hint}
          </p>
        ) : null}
        <div ref={fit.contentRef} className="w-full animate-slide-fade-in">
          {children}
        </div>
      </div>
    </div>
  )
}
